import { EyeOutlined } from "@ant-design/icons";
import {
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  message,
} from "antd";
import HandleNotification from "common/Notification";
import { putData } from "components/BOM/AddOrUpdateStyle";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useReducer,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  SALES_CONTRACT_LIST,
  TNA_PLAN_BASE,
  TNA_PLAN_PURCHASE_ORDER_DETAILS,
  TNA_PLAN_STYLES_DETAILS,
  TNA_POS,
  TNA_SALES_CONTRACT,
  TNA_STYLES,
  TNA_TEMPLATE_BASE,
} from "../../../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../../../apiServices/common";
import {
  cglFormatDate,
  isArrayAndHasValue,
} from "../../../../../utils/functions";
import ItemDetails from "../ItemDetails";
import ListView from "../ListView.js/ListView";
import CustomDatePicker from "common/CustomDatePicker";

const AddOrUpdateForm = forwardRef((props, ref) => {
  const { view, isEdit } = props;

  const [itemDetailsList, setItemDetailsList] = useState([]);
  const [salesContractList, setSalesContractList] = useState(null);
  const [salesContract, setSalesContract] = useState(null);
  const [TNATemplateList, setTNATemplateList] = useState(null);
  const [TNAStyles, setTNAStyles] = useState(null);
  const [TNAPos, setTNAPos] = useState(null);
  const [listTableData, updateListTableDatas] = useReducer(
    (data, partialData) => ({
      ...data,
      ...partialData,
    }),
    {
      isLoading: false,
      salesContractId: null,
      isModalOpen: false,
      listType: null,
      modalTitle: null,
      styleData: null,
      poData: null,
    },
  );

  const { planID } = useParams();
  const navigate = useNavigate();

  const { Option } = Select;
  const [form] = Form.useForm();

  const onSelectSalesContract = useCallback(
    async (value) => {
      const query = `${TNA_SALES_CONTRACT}/${value}`;
      const response = await getData(query);

      if (response && response?.data?.code === 200) {
        const masterData = response?.data?.data;
        setSalesContract(masterData);
        const sales_contract_data = masterData?.sales_contract;

        form.setFieldsValue({
          buyer_id: sales_contract_data?.buyer?.id,
          buyer_name: sales_contract_data?.buyer?.short_name
            ? sales_contract_data?.buyer?.short_name
            : sales_contract_data?.buyer?.name,
          buying_office_id: sales_contract_data?.buying_office?.id,
          buying_office_name: sales_contract_data?.buying_office?.short_name
            ? sales_contract_data?.buying_office?.short_name
            : sales_contract_data?.buying_office?.name,
          contract_date: sales_contract_data?.contract_date,
          order_quantity: masterData?.order_quantity,
          style_no: masterData?.style_detail_count,
          po_no: masterData?.purchase_order_count,

          plan_cut_dates_raw: masterData?.plan_cut_date,
          plan_cut_dates: masterData?.plan_cut_date.map((item) =>
            cglFormatDate(item),
          ),
          shipment_dates: masterData?.shipment_dates.map((item) =>
            cglFormatDate(item),
          ),
        });

        getTNAStyles(sales_contract_data?.id);
        getTNAPos(sales_contract_data?.id);
      } else {
        message.error("Error getting sales contract data");
      }
    },
    [form],
  );

  const findSmallestDate = (dateArray) => {
    const dateObjects = dateArray.map((date) => new Date(date));
    const minDate = new Date(Math.min(...dateObjects));
    return minDate.toISOString().split("T")[0];
  };

  const getTemplateData = useCallback(
    async (value) => {
      try {
        updateListTableDatas({ isLoading: true });

        let plan_cut_date = salesContract?.plan_cut_date;
        plan_cut_date = findSmallestDate(plan_cut_date);
        const endpoint = `${TNA_TEMPLATE_BASE}/${value}?earliest_plan_cut_date=${plan_cut_date}&module=tna-plan`;
        const response = await getData(endpoint);

        if (response && response?.data?.code === 200) {
          const masterData = response?.data?.data;

          form.setFieldsValue({
            description: masterData?.description,
            calculation_method: masterData?.calculation_method,
          });

          const templateTaskData = isArrayAndHasValue(
            masterData?.template_tasks,
          )
            ? masterData?.template_tasks
            : [];

          const updatedTemplateTaskData = [];

          templateTaskData.forEach((task) => {
            if (isArrayAndHasValue(task?.task_dates)) {
              task.task_dates.forEach((dateEntry, index) => {
                const formattedDateEntry = {
                  ...dateEntry,
                  styles: dateEntry.styles || [],
                  purchase_orders: dateEntry.purchase_orders || [],
                };

                updatedTemplateTaskData.push({
                  id: task?.id,
                  tna_task_id: task?.tna_task_id,
                  tna_task_name: task?.title,
                  responsible_person_id: task?.responsible_person_id,
                  responsible_person_name: task?.responsible_person_name,
                  tna_process_title: task?.process,
                  tna_department_name: task?.department,
                  relation_with: task?.relation_with,
                  style_wise: task?.relation_with === "Style" ? "Yes" : "No",
                  po_wise: task?.relation_with === "PO" ? "Yes" : "No",
                  is_pcd: task?.is_pcd === 1 ? "Yes" : "No",
                  is_ship: task?.is_shipment === 1 ? "Yes" : "No",
                  is_add: true,
                  days: dateEntry?.days || 0,
                  due_date: dateEntry?.due_date,
                  task_dependencies:
                    task?.dependencies?.map((item) => item?.id) || [],
                  task_dependencies_names:
                    task?.dependencies?.map((item) => item?.title) || [],

                  task_dates: task?.task_dates || [],

                  style_ids:
                    task?.relation_with === "Style"
                      ? dateEntry?.styles?.map((style) => style?.id) || []
                      : [],
                  style_names:
                    task?.relation_with === "Style"
                      ? dateEntry?.styles?.map((style) => style?.style_no) || []
                      : [],

                  po_ids:
                    task?.relation_with === "PO"
                      ? dateEntry?.purchase_orders?.map((po) => po?.id) || []
                      : [],
                  po_names:
                    task?.relation_with === "PO"
                      ? dateEntry?.purchase_orders?.map(
                          (po) => po?.original_po,
                        ) || []
                      : [],

                  _taskId: task?.id,
                  _isFirstInGroup: index === 0,
                  _groupRowCount: task.task_dates.length,
                });
              });
            } else {
              updatedTemplateTaskData.push({
                id: task?.id,
                tna_task_id: task?.tna_task_id,
                tna_task_name: task?.title,
                responsible_person_id: task?.responsible_person_id,
                responsible_person_name: task?.responsible_person_name,
                tna_process_title: task?.process,
                tna_department_name: task?.department,
                relation_with: task?.relation_with,
                style_wise: task?.relation_with === "Style" ? "Yes" : "No",
                po_wise: task?.relation_with === "PO" ? "Yes" : "No",
                is_pcd: task?.is_pcd === 1 ? "Yes" : "No",
                is_ship: task?.is_shipment === 1 ? "Yes" : "No",
                is_add: true,
                days: task?.days || 0,
                due_date: task?.due_date || "",
                task_dates: [],
                task_dependencies:
                  task?.dependencies?.map((item) => item?.id) || [],
                task_dependencies_names:
                  task?.dependencies?.map((item) => item?.title) || [],
                style_ids: [],
                style_names: [],
                po_ids: [],
                po_names: [],

                _taskId: task?.id,
                _isFirstInGroup: true,
                _groupRowCount: 1,
              });
            }
          });

          setItemDetailsList(updatedTemplateTaskData);
        } else {
          message.error("Error getting template data");
        }
      } catch (error) {
        console.error("Error in getTemplateData:", error);
        message.error("Failed to load template data");
      } finally {
        updateListTableDatas({ isLoading: false });
      }
    },
    [form, salesContract, updateListTableDatas],
  );

  const getTNAPlanData = useCallback(
    async (value) => {
      const endpoint = `${TNA_PLAN_BASE}/${value}`;
      const response = await getData(endpoint);

      if (response && response?.data?.code === 200) {
        const masterData = response?.data?.data;
        const sales_contract_data = masterData?.sales_contract;
        const tna_template_data = masterData?.tna_template;

        form.setFieldsValue({
          sales_contract_id: sales_contract_data?.id,
          template_id: masterData?.tna_template_id,
          description: tna_template_data?.description,
          calculation_method: tna_template_data?.calculation_method,
        });

        onSelectSalesContract(sales_contract_data?.id);

        const taskPlanData = isArrayAndHasValue(masterData?.plan_tasks)
          ? masterData?.plan_tasks
          : [];

        const updatedTaskPlanData = [];

        taskPlanData.forEach((task) => {
          if (isArrayAndHasValue(task?.task_dates)) {
            task.task_dates.forEach((dateEntry, index) => {
              updatedTaskPlanData.push({
                id: task?.id,
                tna_task_id: task?.tna_task_id,
                tna_task_name: task?.title,
                responsible_person_id: task?.responsible_person_id,
                responsible_person_name: task?.responsible_person_name,
                tna_process_title: task?.process,
                tna_department_name: task?.department,
                relation_with: task?.relation_with,
                style_wise: task?.relation_with === "Style" ? "Yes" : "No",
                po_wise: task?.relation_with === "PO" ? "Yes" : "No",
                is_pcd: task?.is_pcd === 1 ? "Yes" : "No",
                is_ship: task?.is_shipment === 1 ? "Yes" : "No",
                is_add: true,
                days: dateEntry?.days || 0,
                due_date: dateEntry?.due_date,
                task_dependencies: task?.task_dependencies?.map(
                  (item) => item?.id,
                ),
                task_dependencies_names: task?.task_dependencies?.map(
                  (item) => item?.title,
                ),

                style_ids:
                  task?.relation_with === "Style"
                    ? dateEntry?.styles?.map((style) => style?.id)
                    : [],
                style_names:
                  task?.relation_with === "Style"
                    ? dateEntry?.styles?.map((style) => style?.style_no)
                    : [],

                po_ids:
                  task?.relation_with === "PO"
                    ? dateEntry?.purchase_orders?.map((po) => po?.id)
                    : [],
                po_names:
                  task?.relation_with === "PO"
                    ? dateEntry?.purchase_orders?.map((po) => po?.original_po)
                    : [],

                _taskId: task?.id,
                _isFirstInGroup: index === 0,
                _groupRowCount: task.task_dates.length,
              });
            });
          } else {
            updatedTaskPlanData.push({
              id: task?.id,
              tna_task_id: task?.tna_task_id,
              tna_task_name: task?.title,
              responsible_person_id: task?.responsible_person_id,
              responsible_person_name: task?.responsible_person_name,
              tna_process_title: task?.process,
              tna_department_name: task?.department,
              relation_with: task?.relation_with,
              style_wise: task?.relation_with === "Style" ? "Yes" : "No",
              po_wise: task?.relation_with === "PO" ? "Yes" : "No",
              is_pcd: task?.is_pcd === 1 ? "Yes" : "No",
              is_ship: task?.is_shipment === 1 ? "Yes" : "No",
              is_add: true,
              days: 0,
              due_date: "",
              task_dependencies: task?.task_dependencies?.map(
                (item) => item?.id,
              ),
              task_dependencies_names: task?.task_dependencies?.map(
                (item) => item?.title,
              ),
              style_ids: [],
              style_names: [],
              po_ids: [],
              po_names: [],

              _taskId: task?.id,
              _isFirstInGroup: true,
              _groupRowCount: 1,
            });
          }
        });

        setItemDetailsList(updatedTaskPlanData);
        updateListTableDatas({ salesContractId: sales_contract_data?.id });
      }
    },
    [onSelectSalesContract, form],
  );

  useEffect(() => {
    if (isEdit && planID) {
      getTNAPlanData(planID);
    }
  }, [isEdit, planID, getTNAPlanData]);

  useImperativeHandle(ref, () => ({
    submit() {
      form
        .validateFields()
        .then(async (values) => {
          console.log("itemDetailsList PUT", itemDetailsList);
          const bodyData = {
            sales_contract_id: values?.sales_contract_id,
            tna_template_id: values?.template_id,
            is_active: 1,
            plan_tasks: itemDetailsList
              .filter((item, index, self) => {
                if (item._taskId) {
                  return (
                    index === self.findIndex((t) => t._taskId === item._taskId)
                  );
                }
                return true;
              })
              .map((item) => {
                console.log("🚀 ~ item:", item);
                const taskData = {
                  ...(isEdit && item?.id && !item?.is_copied
                    ? { id: item?.id }
                    : {}),
                  tna_task_id: item?.tna_task_id,
                  responsible_person_id: item?.responsible_person_id,
                  task_dependencies: item?.task_dependencies || [],
                };

                const allTaskRows = itemDetailsList.filter(
                  (row) => row._taskId === item._taskId,
                );
                const taskRows = allTaskRows.length > 0 ? allTaskRows : [item];

                if (
                  item?.relation_with === "Style" ||
                  item?.style_wise === "Yes"
                ) {
                  taskData.task_styles = taskRows.map((row) => ({
                    style_ids: row?.style_ids || [],
                    days: parseInt(row?.days, 10) || 0,
                  }));

                  if (
                    !taskData.task_styles.length ||
                    taskData.task_styles.every(
                      (entry) => entry.style_ids.length === 0,
                    )
                  ) {
                    taskData.task_styles = [
                      {
                        style_ids: [],
                        days: parseInt(item?.days, 10) || 0,
                      },
                    ];
                  }
                } else if (
                  item?.relation_with === "PO" ||
                  item?.po_wise === "Yes"
                ) {
                  taskData.task_purchase_orders = taskRows.map((row) => ({
                    po_map_ids: row?.po_ids || [],
                    days: parseInt(row?.days, 10) || 0,
                  }));

                  if (
                    !taskData.task_purchase_orders.length ||
                    taskData.task_purchase_orders.every(
                      (entry) => entry.po_map_ids.length === 0,
                    )
                  ) {
                    taskData.task_purchase_orders = [
                      {
                        po_map_ids: [],
                        days: parseInt(item?.days, 10) || 0,
                      },
                    ];
                  }
                } else if (item?.relation_with === "SC") {
                  taskData.task_sales_contracts = [
                    {
                      sales_contract_ids: [],
                      days: parseInt(item?.days, 10) || 0,
                    },
                  ];
                }

                return taskData;
              }),
          };

          if (!isEdit) {
            let response = await postData(TNA_PLAN_BASE, bodyData);
            if (response && response?.code === 200) {
              HandleNotification(
                "success",
                "bottomRight",
                "TNA Created Successfully",
                null,
              );
              const editID = response?.data?.id;
              navigate(`/tanda/update-plan/${editID}`);
            } else {
              // HandleNotification(
              //   "error",
              //   "bottomRight",
              //   "Error Creating TNA",
              //   null,
              // );
            }
          } else {
            const endpoint = `${TNA_PLAN_BASE}/${planID}`;
            let response = await putData(endpoint, bodyData);
            if (response && response?.data?.code === 200) {
              HandleNotification(
                "success",
                "bottomRight",
                "TNA Updated Successfully",
                null,
              );
              await getTNAPlanData(planID);
            } else {
              // HandleNotification(
              //   "error",
              //   "bottomRight",
              //   "Error Updating TNA",
              //   null,
              // );
            }
          }
        })
        .catch((errorInfo) => {
          HandleNotification(
            "error",
            "bottomRight",
            "Error! Please check the form and try again.",
            null,
          );
        });
    },
    discart() {
      form.resetFields();
    },
  }));

  const getSalesContractList = async (filterValues) => {
    const query = `${SALES_CONTRACT_LIST}`;
    const bodyData = {
      per_page: isEdit ? 10000 : 1000,
      name: filterValues?.name || "",
    };
    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setSalesContractList(response?.data?.data?.data);
    } else {
      message.error("Error getting sales contract data");
    }
  };

  const getAllTNATemplates = async (filterValues) => {
    const { name } = filterValues || {};

    const query = `${TNA_TEMPLATE_BASE}?per_page=1000`;
    const bodyData = {
      ...(name && { title: name }),
    };

    const response = await getData(query, false, bodyData);
    if (response && response?.status === 200) {
      setTNATemplateList(response?.data?.data);
    } else {
      message.error("Error getting template list data");
    }
  };

  useEffect(() => {
    getSalesContractList();
    getAllTNATemplates();
  }, []);

  const getTNAStyles = async (salesContractID) => {
    const query = `${TNA_STYLES}/${salesContractID}`;
    const response = await getData(query);

    if (response && response?.data?.code === 200) {
      setTNAStyles(response?.data?.data);
    } else {
      message.error("Error getting TNA styles data");
    }
  };

  const getTNAPos = async (salesContractID) => {
    const query = `${TNA_POS}/${salesContractID}`;
    const response = await getData(query);

    if (response && response?.data?.code === 200) {
      setTNAPos(response?.data?.data);
    } else {
      message.error("Error getting TNA pos data");
    }
  };

  const handleModalCancel = () => {
    updateListTableDatas({
      listType: null,
      modalTitle: null,
      styleData: null,
      poData: null,
      isModalOpen: false,
    });
  };
  const handleModalOk = () => {
    updateListTableDatas({
      listType: null,
      modalTitle: null,
      styleData: null,
      poData: null,
      isModalOpen: false,
    });
  };

  const handleModalClick = async (type) => {
    updateListTableDatas({ isLoading: true });
    if (isEdit && listTableData?.salesContractId) {
      const url =
        type === "style"
          ? TNA_PLAN_STYLES_DETAILS
          : TNA_PLAN_PURCHASE_ORDER_DETAILS;
      let response = await getData(
        `${url}/${listTableData?.salesContractId}`,
        null,
        {
          per_page: 99999,
          view_details: 1,
        },
      );
      await new Promise((r) => setTimeout(r, 500));
      if (type === "style") {
        updateListTableDatas({
          listType: type,
          modalTitle: "Style Details",
          styleData: response?.data?.data,
          isModalOpen: true,
        });
      } else {
        updateListTableDatas({
          listType: type,
          modalTitle: "PO List",
          poData: response?.data?.data,
          isModalOpen: true,
        });
      }
    }
    updateListTableDatas({ isLoading: false });
  };

  useEffect(() => {
    if (!form.getFieldValue("sales_contract_id")) {
      form.setFieldsValue({ template_id: undefined });
    }
  }, [form]);

  return (
    <div className="containt-body">
      <Spin spinning={listTableData?.isLoading}>
        <Form layout="vertical" form={form} name="control-hooks">
          <Card>
            <Row gutter={6}>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Sales Contract"
                  name="sales_contract_id"
                  rules={[
                    {
                      required: true,
                      message: "Please Select Sales Contract",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Select a Sales Contract"
                    optionFilterProp="children"
                    disabled={view}
                    size="small"
                    onSearch={(value) => getSalesContractList({ name: value })}
                    allowClear
                    onClear={() => {
                      form.setFieldsValue({
                        template_id: undefined,
                        buyer_id: undefined,
                        buyer_name: undefined,
                        buying_office_id: undefined,
                        buying_office_name: undefined,
                        contract_date: undefined,
                        order_quantity: undefined,
                        style_no: undefined,
                        po_no: undefined,
                        plan_cut_dates: undefined,
                        shipment_dates: undefined,
                        description: undefined,
                        calculation_method: undefined,
                      });
                      setItemDetailsList([]);
                      getSalesContractList();
                    }}
                    onSelect={(value) => onSelectSalesContract(value)}
                  >
                    {isArrayAndHasValue(salesContractList) &&
                      salesContractList.map((sup) => (
                        <Option value={sup.id} key={sup.id}>
                          {sup.reference_no}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item name="buyer_id" hidden />
                <Form.Item label="Buyer" name="buyer_name">
                  <Input
                    className="w-100"
                    size="small"
                    placeholder="Buyer"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item name="buying_office_id" hidden />
                <Form.Item label="Buying Office" name="buying_office_name">
                  <Input
                    className="w-100"
                    size="small"
                    placeholder="Buying Office"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item label="Sales Contract Date" name="contract_date">
                  <CustomDatePicker
                    className="w-100"
                    size="small"
                    placeholder="Sales Contract Date"
                    disabled
                  />
                  {/* <Input
                    className="w-100"
                    size="small"
                    placeholder="Sales Contract Date"
                    disabled
                  /> */}
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item label="Order Qty (pc.)" name="order_quantity">
                  <Input
                    className="w-100"
                    size="small"
                    placeholder="Order Qty (pc.)"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <div className="flex-row">
                  <EyeOutlined
                    className="cursore-pointer mr-2"
                    onClick={() => handleModalClick("style")}
                  />
                  <Form.Item label="No. of Style" name="style_no">
                    <Input
                      className="w-100"
                      size="small"
                      placeholder="No. of Style"
                      disabled
                    />
                  </Form.Item>
                </div>
              </Col>
              <Col className="gutter-row" span={4}>
                <div className="flex-row">
                  <EyeOutlined
                    className="cursore-pointer mr-2"
                    onClick={() => handleModalClick("po")}
                  />
                  <Form.Item label="No. of PO" name="po_no">
                    <Input
                      className="w-100"
                      size="small"
                      placeholder="No. of PO"
                      disabled
                    />
                  </Form.Item>
                </div>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item label="Plan Cut Dates(s)" name="plan_cut_dates">
                  <Input
                    className="w-100"
                    size="small"
                    placeholder="Plan Cut Dates(s)"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item label="Shipment Dates(s)" name="shipment_dates">
                  <Input
                    className="w-100"
                    size="small"
                    placeholder="Shipment Dates(s)"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  name="template_id"
                  label="Template Title"
                  rules={[
                    {
                      required: true,
                      message: "Please Select Template Title",
                    },
                  ]}
                >
                  <Select
                    dropdownStyle={{ minWidth: 250 }}
                    showSearch
                    onFocus={(e) => {}}
                    placeholder="Copy from Template Title"
                    optionFilterProp="children"
                    onSelect={(value) => {
                      getTemplateData(value);
                    }}
                    style={{ width: "100%" }}
                    size="small"
                    disabled={view || !form.getFieldValue("sales_contract_id")}
                    onSearch={(value) => getAllTNATemplates({ title: value })}
                  >
                    {isArrayAndHasValue(TNATemplateList) &&
                      TNATemplateList.map((item) => (
                        <Option value={item.id} key={item.id}>
                          {item?.title}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item label="Description" name="description">
                  <Input
                    className="w-100"
                    size="small"
                    placeholder="Description"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item label="Calculation Method" name="calculation_method">
                  <Input
                    className="w-100"
                    size="small"
                    placeholder="Calculation Method"
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
          <ItemDetails
            view={view}
            itemDetailsList={itemDetailsList}
            setItemDetailsList={setItemDetailsList}
            form={form}
            TNAStyles={TNAStyles}
            TNAPos={TNAPos}
          />
        </Form>

        <Modal
          title={listTableData?.modalTitle}
          open={listTableData?.isModalOpen}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          width={1000}
        >
          <ListView datas={listTableData} />
        </Modal>
      </Spin>
    </div>
  );
});

export default AddOrUpdateForm;
